import { Box, Card, List, ListItem, ListItemText, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';
import type { IIsPdf } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { AvaniNumberSpin } from '../DataDisplay';

interface AvaniRankingCardProps extends IIsPdf {
  title: string;
  rankingValue: number;
  lastVisitValue: number;
  changeValue: number;
}

const AvaniRankingStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px 0 8px 0;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0 0 0 16px;
    line-height: 1;
    padding-bottom: 0;
  }

  .animated-container {
    margin-top: -11px;
  }
`
);

export const AvaniRankingCard: FC<AvaniRankingCardProps> = ({
  title,
  rankingValue,
  lastVisitValue,
  changeValue,
  isPDF = false
}) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();

  const formatPercentageNumber = (value: number): number => Number((value * 100).toFixed(2));

  return (
    <AvaniRankingStyledCard
      raised={true}
      sx={{ height: '100%', backgroundColor: theme.colors.avani.lightTurc, ['.MuiPieArc-root']: { stroke: 'none' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: '11px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h3" color={theme.colors.avani.teal} sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
          </Box>
          <AvaniNumberSpin
            isRanking
            fSize={40}
            numberValue={formatPercentageNumber(rankingValue)}
            preserveTailing
            isPercentage
            isPDF={isPDF}
          />
        </Box>
        <List
          dense
          sx={{
            padding: '0 0 0 18px',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}
        >
          <ListItem
            sx={{ p: 0 }}
            secondaryAction={
              <Typography variant="body2" fontSize={16}>
                {`${formatPercentageNumber(lastVisitValue).toFixed(2)} %`}
              </Typography>
            }
          >
            <ListItemText>
              <Typography variant="body2" fontSize={16}>
                Last Visit
              </Typography>
            </ListItemText>
          </ListItem>
          <ListItem
            sx={{ p: 0 }}
            secondaryAction={
              <Typography
                fontSize={16}
                variant="body2"
                sx={{ color: `${changeValue > 0 ? theme.colors.avani.turc : theme.colors.avani.magenta}` }}
              >
                {`${formatPercentageNumber(changeValue).toFixed(2)} %`}
              </Typography>
            }
          >
            <ListItemText>
              <Typography
                fontSize={16}
                variant="body2"
                sx={{ color: `${changeValue > 0 ? theme.colors.avani.turc : theme.colors.avani.magenta}` }}
              >
                Change
              </Typography>
            </ListItemText>
          </ListItem>
        </List>
        <Typography paddingX={2} variant="body2" fontSize={13}>
          Venue is marked against core metrics to obtain a score. Use the rating to monitor performance trends or
          benchmark against other venues.
        </Typography>
      </Box>
    </AvaniRankingStyledCard>
  );
};
